import {
  getValidationErrorLevelColor,
  LastChanged,
  Table,
  Tooltip,
  useSystemClassifierItemsBy,
  ValidationErrorType,
} from '@atlas-frontend/sdk';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { Badge, Flex } from 'antd';
import { FC, memo, PropsWithChildren, useMemo, useState } from 'react';
import { path } from '../../../paths';
import { UiLink } from '../../../paths/UiLink';
import { ValidationErrorDetailsModal } from './ValidationErrorDetailsModal';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './ValidationErrorsTable.translations.json';
import { ClippedContentCell } from './ClippedContentCell';
import * as Icon from 'react-feather';
import { colorGray } from '@atlas-frontend/design-tokens';
import { formatErrorDescription } from './utils';
import { useDescriptionFilter } from './useDescriptionFilter';

const { addTranslations, useTranslation } = i18nNsFactory(
  'validation-errors-table',
);
addTranslations(translations);

export interface ValidationErrorsTableProps extends PropsWithChildren {
  validationErrors: ValidationErrorType[];
  scrollContainerSelector: string;
}

export const ValidationErrorsTable: FC<ValidationErrorsTableProps> = (
  props,
) => {
  const { validationErrors, scrollContainerSelector } = props;
  const { t } = useTranslation();

  const systemClassifierItemsById = useSystemClassifierItemsBy([
    'PROC_VAL_ERROR_LEVEL',
    'VALIDATION_RULE_OPERATOR',
  ]);

  const [errorDetails, setErrorDetails] = useState<ValidationErrorType>();
  const { filters, setFilters } = useDescriptionFilter({
    data: validationErrors,
    filterId: 'processingDataObjectName',
    getDescriptionId: (error) => error.processingDataObjectDescriptionId,
    getFilterValue: (error) => error.processingDataObjectName,
  });

  const ClippedContentCellWithShowMore = memo(
    (props: CellContext<ValidationErrorType, any>) => (
      <ClippedContentCell
        {...props}
        onShowMore={() => setErrorDetails(props.row.original)}
      />
    ),
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor((_, index) => index + 1, {
        id: 'sequenceNumber',
        header: t('column.sequenceNumber'),
        cell: (props) => props.renderValue(),
        enableColumnFilter: false,
        meta: { width: '12ch', className: 'sticky-left', textAlign: 'right' },
      }),
      columnHelper.accessor('processingDataObjectName', {
        header: t('column.processingDataObjectName'),
        filterFn: 'select',
        meta: { width: '18ch', className: 'sticky-left' },
      }),
      columnHelper.accessor('ruleCode', {
        header: t('column.ruleCode'),
        cell: RuleCodeCell,
        meta: { width: '24ch' },
      }),
      columnHelper.accessor(
        (rowData) =>
          systemClassifierItemsById.PROC_VAL_ERROR_LEVEL.get(
            rowData.errorLevelId,
          )?.name,
        {
          header: t('column.errorLevelId'),
          cell: (props) => (
            <ErrorsLevelCell
              {...props}
              {...systemClassifierItemsById.PROC_VAL_ERROR_LEVEL.get(
                props.row.original.errorLevelId,
              )}
            />
          ),
          meta: { width: '15ch' },
        },
      ),
      columnHelper.accessor(
        (rowData) => t(`column.ignoreError.${rowData.ignoreError}`),
        {
          header: t('column.ignoreError.header'),
          meta: { width: '15ch' },
          cell: IgnoreErrorCell,
        },
      ),
      columnHelper.accessor(
        (rowData) => formatErrorDescription(rowData.description),
        {
          id: 'description',
          header: t('column.description.header'),
          enableSorting: false,
          cell: ClippedContentCellWithShowMore,
          meta: { width: '30ch' },
        },
      ),
      columnHelper.accessor('condition', {
        header: t('column.condition'),
        cell: ClippedContentCellWithShowMore,
        meta: { width: '30ch' },
      }),
      columnHelper.accessor('expressionLeftHandSide', {
        header: t('column.expressionLeftHandSide'),
        cell: ClippedContentCellWithShowMore,
        meta: { width: '30ch' },
      }),
      columnHelper.accessor(
        (rowData) =>
          rowData.expressionOperatorId &&
          systemClassifierItemsById.VALIDATION_RULE_OPERATOR.get(
            rowData.expressionOperatorId,
          )?.code,
        {
          header: t('column.expressionOperatorId'),
          meta: { width: '17ch' },
        },
      ),
      columnHelper.accessor('expressionRightHandSide', {
        header: t('column.expressionRightHandSide'),
        cell: ClippedContentCellWithShowMore,
        meta: { width: '30ch' },
      }),
      columnHelper.accessor('tolerance', {
        header: t('column.tolerance'),
        meta: { width: '17ch' },
      }),
    ],
    [
      t,
      ClippedContentCellWithShowMore,
      systemClassifierItemsById.PROC_VAL_ERROR_LEVEL,
      systemClassifierItemsById.VALIDATION_RULE_OPERATOR,
    ],
  );

  const options = useMemo(
    () => ({
      processingDataObjectName: [
        ...new Set(validationErrors.map((e) => e.processingDataObjectName)),
      ].map((name) => ({ id: name, name })),
    }),
    [validationErrors],
  );

  return (
    <>
      <Table
        name="validationErrors"
        columns={columns}
        data={validationErrors}
        options={options}
        filters={filters}
        onFilter={setFilters}
        sticky
        scrollContainerSelector={scrollContainerSelector}
      />
      {errorDetails && (
        <ValidationErrorDetailsModal
          error={errorDetails}
          systemClassifierItemsById={systemClassifierItemsById}
          onClose={() => setErrorDetails(undefined)}
        />
      )}
    </>
  );
};

const columnHelper = createColumnHelper<ValidationErrorType>();

interface ErrorsLevelCellProps extends CellContext<ValidationErrorType, any> {
  code?: string;
  name?: string;
}

const ErrorsLevelCell: FC<ErrorsLevelCellProps> = memo(
  (props) =>
    props.code && (
      <>
        <Badge color={getValidationErrorLevelColor(props.code)} /> {props.name}
      </>
    ),
);

const IgnoreErrorCell: FC<CellContext<ValidationErrorType, any>> = memo(
  (props) => {
    const { ignoreError, ignoreReason } = props.row.original;
    return (
      <Flex gap="small">
        {props.renderValue()}
        {ignoreError && ignoreReason && (
          <Tooltip
            title={ignoreReason}
            children={<Icon.MessageCircle size={18} color={colorGray[6]} />}
          />
        )}
      </Flex>
    );
  },
);

const RuleCodeCell: FC<CellContext<ValidationErrorType, any>> = memo(
  (props) => {
    const {
      validationRuleId,
      validationRuleModifiedAt,
      modifiedAt,
      processingDataObjectDescriptionVersionId,
    } = props.row.original;

    const displayValue = props.renderValue();

    if (!validationRuleId) {
      return displayValue;
    }

    const isValidationRuleModifiedAfterError =
      validationRuleModifiedAt &&
      modifiedAt &&
      new Date(validationRuleModifiedAt) > new Date(modifiedAt);

    const link = (
      <UiLink
        id={`validation-rule-link-${validationRuleId}`}
        path={path.outputDataObjectView}
        params={{
          id: processingDataObjectDescriptionVersionId,
          segment: 'validationRules',
          validationRuleId,
        }}
        target="_blank"
      >
        {displayValue}
      </UiLink>
    );

    return isValidationRuleModifiedAfterError ? (
      <Tooltip title={<LastChanged lastChangedAt={validationRuleModifiedAt} />}>
        {link}
      </Tooltip>
    ) : (
      link
    );
  },
);
